import React from 'react';
import type { Worker } from '../../engine/data';
import { pliLabel, psiLabel, roleTypeLabel } from '../../engine/scores';
import { pliTone, psiTone } from '../../engine/scores';
import { Banner, FBar, Panel, toneSurface, toneText } from '../../ui';
import type { Tone } from '../../ui';

const Component: React.FC<{
  label: string;
  weight: string;
  value: React.ReactNode;
  bar: number;
  tone: Tone;
  detail: React.ReactNode;
}> = ({ label, weight, value, bar, tone, detail }) => (
  <div>
    <div className='mb-1 flex justify-between text-xs'>
      <span className='text-muted-foreground'>
        {label} <span className='text-[10px]'>({weight} weight)</span>
      </span>
      <span className='font-semibold'>{value}</span>
    </div>
    <FBar value={bar} tone={tone} />
    <div className='mt-0.5 text-[10px] text-muted-foreground'>{detail}</div>
  </div>
);

const BigScore: React.FC<{ value: number; tone: Tone; caption: string }> = ({ value, tone, caption }) => (
  <>
    <div className={`text-5xl font-bold leading-none ${toneText[tone]}`}>{value}</div>
    <div className='mt-1 text-[11px] uppercase tracking-wider text-muted-foreground'>{caption}</div>
  </>
);

export const PliPanel: React.FC<{ w: Worker }> = ({ w }) => (
  <Panel
    title='PLI — Physical Load Index'
    sub='Shift physical demand: Karvonen %HR Reserve · Autonomic depletion · EDA sympathetic load'
  >
    <div className='flex flex-wrap gap-6 pb-2'>
      <div className='shrink-0'>
        <BigScore value={w.pli} tone={pliTone(w.pli)} caption={`${pliLabel(w.pli)} load`} />
        <div className='mt-2 text-[13px] text-muted-foreground'>
          ~<strong className='text-foreground'>{w.pliKcal?.toLocaleString() || '—'}</strong> kcal est.
        </div>
        <div className='mt-0.5 text-[11px] text-muted-foreground'>
          {w.gender === 'F' ? 'Keytel (F)' : 'Keytel (M)'} · {w.age}yr · {w.weight_kg}kg
        </div>
      </div>
      <div className='flex min-w-[180px] flex-1 flex-col justify-center gap-2.5'>
        <Component
          label='HRR Load'
          weight='50%'
          value={`${w.pliHRR}%`}
          bar={w.pliHRR}
          tone='info'
          detail={`Avg HR ${w.hr_work}bpm · max ${220 - (w.age || 35)}bpm · RHR ${w.rhr}bpm`}
        />
        <Component
          label='Autonomic Depletion'
          weight='30%'
          value={w.pliAuto}
          bar={w.pliAuto}
          tone='warn'
          detail={`HRV ${w.hrv_pre}→${w.hrv_post}ms · Rec HR ${w.hr_rec} vs ${w.hr_rec_b}bpm base`}
        />
        <Component
          label='EDA Sympathetic Load'
          weight='20%'
          value={w.pliEDA}
          bar={w.pliEDA}
          tone='violet'
          detail='Electrodermal activity proxy · sympathetic nervous system activation'
        />
      </div>
    </div>
    {w.pli >= 75 ? (
      <Banner tone='bad' className='text-xs'>
        Very high physical load this week. Consider reduced shift intensity or recovery protocol.
      </Banner>
    ) : w.pli >= 55 ? (
      <Banner tone='warn' className='text-xs'>
        High physical load. Monitor closely — HR and autonomic recovery data suggest elevated demand.
      </Banner>
    ) : (
      <Banner tone='info' className='text-xs'>
        Physical load within acceptable range. Continue monitoring HRV and recovery HR post-shift.
      </Banner>
    )}
  </Panel>
);

export const PsiPanel: React.FC<{ w: Worker }> = ({ w }) => {
  const white = w.roleType === 'white';
  const blue = w.roleType === 'blue';
  const roleTone: Tone = white ? 'violet' : blue ? 'good' : 'warn';
  const hrElev = w.hr_work_b > 0 ? Math.round(((w.hr_work - w.hr_work_b) / w.hr_work_b) * 100) : 0;

  return (
    <Panel
      title='PSI — Psychological Stress Index'
      sub={
        <>
          Cognitive & emotional load: Autonomic dysregulation · EDA cognitive arousal · Work pattern pressure ·{' '}
          <span className={`font-semibold ${toneText[roleTone]}`}>{roleTypeLabel(w.roleType)}</span>
        </>
      }
    >
      <div className='flex flex-wrap gap-6 pb-2'>
        <div className='shrink-0'>
          <BigScore value={w.psi} tone={psiTone(w.psi)} caption={`${psiLabel(w.psi)} stress`} />
          <div className={`mt-2.5 max-w-[240px] rounded-lg border px-2.5 py-1.5 text-[11px] ${toneSurface[roleTone]}`}>
            <div className='mb-0.5 font-semibold text-foreground'>{roleTypeLabel(w.roleType)}</div>
            <div className='text-muted-foreground'>
              {white
                ? 'Psych dominant — autonomic & cognitive signals weighted higher'
                : blue
                  ? 'Physical dominant — pattern pressure & work schedule weighted higher'
                  : 'Mixed — balanced weighting across all three components'}
            </div>
          </div>
        </div>
        <div className='flex min-w-[180px] flex-1 flex-col justify-center gap-2.5'>
          <Component
            label='Autonomic Dysregulation'
            weight={white ? '42%' : '35%'}
            value={w.psiAuto}
            bar={w.psiAuto}
            tone='violet'
            detail={`HRV ${w.hrv}ms vs ${w.hrv_b}ms base · RHR ${w.rhr} vs ${w.rhr_b}bpm base${!white ? ' · physical HRR confound removed' : ''}`}
          />
          <Component
            label='Cognitive Arousal'
            weight={white ? '38%' : '30%'}
            value={w.psiCog}
            bar={w.psiCog}
            tone='info'
            detail={`EDA ${w.edaScore} · HR elevation ${hrElev}% vs base · psychological fraction isolated`}
          />
          <Component
            label='Work Pattern Pressure'
            weight={white ? '20%' : '35%'}
            value={w.psiPat}
            bar={w.psiPat}
            tone='warn'
            detail={`${w.consec} consecutive days · ${w.otHrs}h OT this week · ${w.normalHrs}h contracted`}
          />
        </div>
      </div>
      {w.psi >= 75 ? (
        <Banner tone='bad' className='text-xs'>
          Very high psychological stress. Consider workload review, wellbeing check-in, and schedule relief.
        </Banner>
      ) : w.psi >= 55 ? (
        <Banner tone='warn' className='text-xs'>
          Elevated psychological stress. Monitor for signs of burnout — HRV pattern and cognitive arousal suggest
          sustained pressure.
        </Banner>
      ) : (
        <Banner tone='info' className='text-xs'>
          Psychological stress within expected range for this role type. Continue monitoring HRV resting baseline for
          early signal.
        </Banner>
      )}
    </Panel>
  );
};
